import React from 'react';
import { produce } from 'immer';
import FileUploader, {
  create as createUploader,
} from '@bynder/js-file-uploader-lib';
import { Status } from '../../constants/upload';

interface Props {
  files: File[];
  intent: string;
  onEstimatedSpeedUpdate?: (bytesPerSecond: number) => void;
  onFileUploadCreated?: (fileUpload: FileUpload) => void;
  onFileUploadPrepared?: (
    id: string,
    fileId: string,
    methods?: FileUploadMethods,
  ) => void;
  onFileUploadRegistered?: (fileId: string) => void;
  onFileUploadProgress?: (fileId: string, progress: number) => void;
  onFileUploadCompleted?: (fileId: string) => void;
  onFileUploadConfirmed?: (fileId: string) => void;
  onFileUploadError?: (fileId: string, error: Error) => void;
  children?: (props: UploadProps) => React.ReactNode;
  isPublic?: boolean;
}

interface UploadProps {
  fileUploads: FileUpload[];
  uploadedCount: number;
  confirmedCount: number;
}

interface FileUploadMethods {
  cancelUpload?: () => void;
  cancelAndRemove?: () => void;
  remove?: () => void;
}
interface State {
  fileUploads: FileUpload[];
  uploadedCount: number;
  confirmedCount: number;
}

interface FileUpload extends FileUploadMethods {
  id: string;
  file: File;
  fileId?: string;
  status: Status;
  progress?: number;
  errorMessage?: string;
}

class Uploader extends React.Component<Props, State> {
  readonly state: State = {
    fileUploads: [],
    uploadedCount: 0,
    confirmedCount: 0,
  };

  Uploader: FileUploader;

  constructor(props: Props) {
    super(props);
    this.Uploader = createUploader(
      {
        onProgress: this.onProgress,
        onRegistered: this.onRegistered,
        onCompleted: this.onCompleted,
        onConfirmed: this.onConfirmed,
        onError: this.onError,
        onFileUploadCreated: this.onUploadCreated,
        onUploadPrepared: this.onUploadPrepared,
        onEstimatedSpeedUpdate: this.onEstimatedSpeedUpdate,
      },
      props.intent,
      props.isPublic,
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.files &&
      this.props.files.length > 0 &&
      prevProps.files !== this.props.files
    ) {
      const addedFiles = this.props.files.filter(
        file => !prevProps.files.includes(file),
      );

      if (addedFiles.length > 0) {
        this.Uploader.newUpload(addedFiles);
      }
    }

    if (!this.props.files.length && this.state.fileUploads.length) {
      // Ignored, because setState is wrapped in a condition
      this.setState({
        fileUploads: [],
      });
    }
  }

  onEstimatedSpeedUpdate = (bytesPerSecond: number) => {
    if (this.props.onEstimatedSpeedUpdate) {
      this.props.onEstimatedSpeedUpdate(bytesPerSecond);
    }
  };

  onUploadPrepared = (id: string, fileId: string) => {
    const fileUploadMethods: FileUploadMethods = {};

    fileUploadMethods.cancelUpload = () => {
      this.Uploader.cancelUpload(fileId);
    };
    fileUploadMethods.cancelAndRemove = () => {
      this.cancelAndRemoveFileUpload(fileId);
    };
    fileUploadMethods.remove = () => {
      this.removeFileUpload(id);
    };

    this.setState(prevState => ({
      ...prevState,
      fileUploads: prevState.fileUploads.map(fileUpload => {
        if (fileUpload.id === id) {
          const fileUploadWithFileId = {
            ...fileUpload,
            ...fileUploadMethods,
            fileId,
          };

          return fileUploadWithFileId;
        }

        return fileUpload;
      }),
    }));

    if (this.props.onFileUploadPrepared) {
      this.props.onFileUploadPrepared(id, fileId, fileUploadMethods);
    }
  };

  onUploadCreated = (fileUpload: { id: string; file: File }) => {
    const newFileUpload = {
      id: fileUpload.id,
      file: fileUpload.file,
      status: Status.Pending,
    };

    this.setState(previousState => ({
      fileUploads: [...previousState.fileUploads, newFileUpload],
    }));

    if (this.props.onFileUploadCreated) {
      this.props.onFileUploadCreated(newFileUpload);
    }
  };

  onRegistered = (fileId: string) => {
    if (this.props.onFileUploadRegistered) {
      this.props.onFileUploadRegistered(fileId);
    }
  };

  onProgress = (fileId: string, progress: number) => {
    if (this.props.onFileUploadProgress) {
      this.props.onFileUploadProgress(fileId, progress);
    }

    this.setState(prevState => ({
      ...prevState,
      fileUploads: prevState.fileUploads.map(fileUpload =>
        fileUpload.fileId === fileId
          ? { ...fileUpload, progress, status: Status.Uploading }
          : fileUpload,
      ),
    }));
  };

  onCompleted = (fileId: string) => {
    if (this.props.onFileUploadCompleted) {
      const fileUpload = this.state.fileUploads.find(
        fu => fu.fileId === fileId,
      );

      this.props.onFileUploadCompleted(fileUpload.file.id || fileId);
    }

    this.setState(
      produce((draft: typeof this.state) => {
        draft.fileUploads = draft.fileUploads.map(fileUpload =>
          fileUpload.fileId === fileId
            ? { ...fileUpload, status: Status.Uploaded }
            : fileUpload,
        );

        draft.uploadedCount += 1;
      }),
    );
  };

  onConfirmed = (fileId: string) => {
    if (this.props.onFileUploadConfirmed) {
      this.props.onFileUploadConfirmed(fileId);
    }

    this.setState(
      produce((draft: typeof this.state) => {
        draft.fileUploads = draft.fileUploads.map(fileUpload =>
          fileUpload.fileId === fileId
            ? { ...fileUpload, status: Status.Confirmed }
            : fileUpload,
        );

        draft.confirmedCount += 1;
      }),
    );
  };

  onError = (fileId: string, error: Error) => {
    if (this.props.onFileUploadError) {
      this.props.onFileUploadError(fileId, error);
    }

    this.setState(prevState => ({
      ...prevState,
      fileUploads: prevState.fileUploads.map(fileUpload =>
        fileUpload.fileId === fileId
          ? {
              ...fileUpload,
              status: Status.Failed,
              errorMessage: error.message,
            }
          : fileUpload,
      ),
    }));
  };

  cancelAndRemoveFileUpload = (fileId: string) => {
    this.Uploader.cancelUpload(fileId);
    this.setState(prevState => ({
      ...prevState,
      fileUploads: prevState.fileUploads.filter(
        fileUpload => fileUpload.fileId !== fileId,
      ),
    }));
  };

  removeFileUpload = (id: string) => {
    this.setState(prevState => ({
      fileUploads: prevState.fileUploads.filter(
        fileUpload => fileUpload.id !== id,
      ),
    }));
  };

  render() {
    return this.props.children
      ? this.props.children({
          fileUploads: this.state.fileUploads,
          uploadedCount: this.state.uploadedCount,
          confirmedCount: this.state.confirmedCount,
        })
      : null;
  }
}

export default Uploader;
